import { Accordion, Clickable, H2, PortableText, TypeWrapper } from '@ahmdigital/ui';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { map } from 'lodash/fp';
import React, { useState } from 'react';

import { AccordionContent, ColumnWrapper, ContentWrapper, SummaryWrapper } from './styles';
import { BlockType } from '../../../types/sanityInternal';
import { ImageType } from '../../../types/image';
import { MappedQuestion } from '../../../types/question';
import accordionActionCreators from '../../../actions/accordion';
import AccordionImage from './components/accordion-image';
import getVisibleAccordionItems from './utils/get-visible-accordion-items';
import styles from './styles.module.sass';
import wrapItemBody from './components/wrap-item-body';
import WrappedSection from '../wrapped-section';

type AccordionSectionProps = {
  accordionId: string;
  actions: {
    accordion: {
      updateAccordionWithId: ({ id, openIndices }: { id: string; openIndices: string[] }) => void;
    };
  };
  ariaLabelPrefix?: string;
  className?: string;
  cta?: {
    title: string;
    to: string;
  };
  image?: ImageType | null;
  initialVisible?: number;
  isTwoColumn?: boolean;
  items?: MappedQuestion[];
  minimalBorder?: boolean;
  openIndices?: number[];
  title?: string;
  spacerSize?: number | 'None';
  summary?: BlockType[];
  withBorder?: boolean;
};

const AccordionSection = ({
  actions,
  accordionId,
  ariaLabelPrefix,
  className,
  cta,
  image,
  minimalBorder,
  openIndices,
  items,
  summary,
  title,
  withBorder,
  isTwoColumn,
  initialVisible,
  spacerSize,
}: AccordionSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  // @ts-expect-error - Automatic, Please fix when editing this file
  const itemsToDisplay = getVisibleAccordionItems({ initialVisible, isOpen, items });
  // @ts-expect-error - Automatic, Please fix when editing this file
  const showSeeMore = initialVisible && items.length > initialVisible;
  const hasHeaderCopy = title || summary;

  return (
    <WrappedSection spacerSize={spacerSize} withBorder={withBorder}>
      {!isTwoColumn && <AccordionImage image={image} />}
      {hasHeaderCopy && (
        <TypeWrapper variant="center">
          {title && <H2 variant={`marginBottomSpacer${summary ? '8' : '6'}`}>{title}</H2>}
          {summary && (
            <SummaryWrapper>
              <PortableText variant="marginBottomSpacer8" source={summary} />
            </SummaryWrapper>
          )}
        </TypeWrapper>
      )}
      <ContentWrapper isTwoColumn={isTwoColumn} data-testid="content-wrapper">
        {isTwoColumn && (
          <ColumnWrapper isTwoColumn={isTwoColumn} data-testid="two-column-image">
            <AccordionImage image={image} isTwoColumn />
          </ColumnWrapper>
        )}
        <ColumnWrapper isTwoColumn={isTwoColumn}>
          <AccordionContent isTwoColumn={isTwoColumn}>
            <Accordion
              className={className}
              id={accordionId}
              ariaLabelPrefix={ariaLabelPrefix}
              items={map(
                (item) => ({
                  ...item,
                  body: wrapItemBody(item.body),
                }),
                itemsToDisplay,
              )}
              updateSelection={(newOpenIndices: string[]) => {
                actions.accordion.updateAccordionWithId({
                  id: accordionId,
                  openIndices: newOpenIndices,
                });
              }}
              variant={[!minimalBorder && 'fullBorder', 'white']}
              {...{ openIndices }}
            />
            {!isOpen && showSeeMore && (
              <TypeWrapper variant={['center', 'marginTopSpacer8']}>
                <Clickable
                  icon={{ fontWeight: 'regular', name: 'chevron-down', position: 'right' }}
                  isUnstyled
                  onClick={() => setIsOpen(true)}
                >
                  View more
                </Clickable>
              </TypeWrapper>
            )}
            {cta?.title && cta?.to && (
              <div className={styles.cta}>
                <Clickable icon={{ fontWeight: 'regular', name: 'chevron-right', position: 'right' }} to={cta.to}>
                  {cta.title}
                </Clickable>
              </div>
            )}
          </AccordionContent>
        </ColumnWrapper>
      </ContentWrapper>
    </WrappedSection>
  );
};

AccordionSection.defaultProps = {
  cta: null,
  image: null,
  initialVisible: null,
  items: [],
  openIndices: [],
  title: '',
  withBorder: false,
};

// @ts-expect-error - Automatic, Please fix when editing this file
const mapStateToProps = (state, { accordionId }) => ({
  openIndices: state?.accordion?.openIndices?.[accordionId],
});

// @ts-expect-error - Automatic, Please fix when editing this file
const mapDispatchToProps = (dispatch) => ({
  actions: {
    accordion: bindActionCreators(accordionActionCreators, dispatch),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AccordionSection);

export { AccordionSection as AccordionSectionForTest };
